import { useState, useEffect } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth, resolveAuthenticatedRedirect } from '@/shared/lib/auth';
import { useBrand } from '@/shared/lib/brand';
import { isNpsSession } from '@/shared/lib/nps-session';
import { ApiError, http } from '@/shared/api/client';
import type { SessionResponse } from '@/shared/types';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { ThemeToggle } from '@/shared/components/theme-toggle';
import { toast } from '@/shared/ui/toaster';
import { loginSchema, type LoginValues } from '../model/schema';

/** Resolve destino pós-login; só faz fetch extra para legacy sem loja fixa. */
async function resolveStartAfterLogin(
  session: SessionResponse,
  redirectUrl?: string | null,
): Promise<string> {
  const path = resolveAuthenticatedRedirect(
    session.user,
    session.authSource,
    session.start,
    redirectUrl,
  );
  const authSource = session.authSource ?? (session.user.keycloak_id ? 'v2' : 'legacy');
  if (isNpsSession(authSource) || path !== '/selecionar-loja') return path;

  try {
    const data = await http.get<{ passbooks?: Array<{ _id: string }> }>(
      `/bff/api/passbookUser/user/find/${session.user._id}`,
    );
    const found = data.passbooks ?? [];
    // Conta de grupo mantém a escolha da marca; uma loja só entra direto.
    if (found.length === 1 && found[0]?._id) return `/loja/${found[0]._id}`;
  } catch {
    /* segue para o seletor */
  }
  return path;
}

export function LoginPage() {
  const { isAuthenticated, isLoading, login, user, authSource, startPath } = useAuth();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirectUrl');
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const redirecting = !isLoading && isAuthenticated && Boolean(user);

  useEffect(() => {
    // Carregando ou já logado, a tela só redireciona: não troca o título da aba.
    if (isLoading || redirecting) return;
    document.title = brand.loginTitle;
    return () => {
      document.title = brand.documentTitle;
    };
  }, [isLoading, redirecting, brand.loginTitle, brand.documentTitle]);

  if (redirecting && user) {
    return (
      <Navigate
        to={resolveAuthenticatedRedirect(user, authSource, startPath, redirectUrl)}
        replace
      />
    );
  }

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const session = await login(values);
      const start = await resolveStartAfterLogin(session, redirectUrl);
      navigate(start, { replace: true });
    } catch (error) {
      const message =
        error instanceof ApiError ? error.message : 'Não foi possível conectar. Tente novamente.';
      toast.error('Falha no login', { description: message });
      form.setError('password', { message: ' ' });
    }
  });

  return (
    <div className="relative flex min-h-dvh flex-col items-center justify-center bg-[#f5f5f5] px-4 py-10 dark:bg-black sm:px-6">
      <ThemeToggle className="absolute right-4 top-4 z-20 rounded-xl border border-black/10 bg-white/90 shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-[#121212]/90 sm:right-6 sm:top-6" />

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 240, damping: 28 }}
        className="w-full max-w-[420px]"
      >
        <div className="overflow-hidden rounded-2xl bg-white shadow-[0_20px_50px_-12px_hsl(var(--primary)/0.18)] ring-1 ring-black/[0.04] dark:bg-[#121212] dark:shadow-none dark:ring-white/10">
          <div className="relative flex h-[148px] items-center overflow-hidden bg-gradient-to-br from-primary/20 via-primary/12 to-primary/5 px-6 dark:from-primary/25 dark:via-primary/12 dark:to-[#121212] sm:h-[156px] sm:px-8">
            <div className="z-10">
              <h1 className="text-[22px] font-bold leading-tight tracking-tight text-[#404040] dark:text-foreground sm:text-2xl">
                Bem vindo !
              </h1>
              <p className="mt-1.5 text-[13px] leading-snug text-[#404040]/75 dark:text-foreground/75 sm:text-sm">
                {brand.loginSubtitle}
              </p>
            </div>
          </div>

          <div className="relative z-10 -mt-9 flex justify-center sm:-mt-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.88, y: 6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 320, damping: 24, delay: 0.08 }}
              className="grid size-[72px] place-items-center rounded-full bg-white shadow-[0_0_0_1px_hsl(var(--primary)/0.15),0_0_10px_2px_hsl(var(--primary)/0.1)] ring-[5px] ring-white dark:bg-[#121212] dark:shadow-[0_0_0_1px_hsl(var(--primary)/0.2),0_0_12px_3px_hsl(var(--primary)/0.1)] dark:ring-[#121212] sm:size-[76px]"
            >
              <img
                src={brand.assets.icon.brand}
                alt={brand.displayName}
                width={44}
                height={44}
                className="size-11 object-contain sm:size-12"
              />
            </motion.div>
          </div>

          <div className="px-6 pb-7 pt-3 sm:px-8 sm:pb-8 sm:pt-4">
            <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="email" className="text-sm font-medium text-[#404040] dark:text-foreground/90">
                  E-mail
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  placeholder="Digite seu email"
                  className="login-field h-11 border-[#e8e8e8] bg-white dark:border-border/70 dark:bg-[#0a0a0a]"
                  aria-invalid={Boolean(form.formState.errors.email)}
                  {...form.register('email')}
                />
                {form.formState.errors.email?.message ? (
                  <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="password" className="text-sm font-medium text-[#404040] dark:text-foreground/90">
                  Senha
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Digite sua senha"
                    className="login-field h-11 border-[#e8e8e8] bg-white pr-11 dark:border-border/70 dark:bg-[#0a0a0a]"
                    aria-invalid={Boolean(form.formState.errors.password)}
                    {...form.register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-1 top-1/2 grid size-10 -translate-y-1/2 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                </div>
                {form.formState.errors.password?.message?.trim() ? (
                  <p className="text-xs text-destructive">{form.formState.errors.password.message}</p>
                ) : null}
              </div>

              <Button
                type="submit"
                size="lg"
                className="mt-1 h-11 w-full rounded-xl text-[15px] font-semibold shadow-sm"
                loading={form.formState.isSubmitting}
              >
                Acessar
              </Button>
            </form>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-[#6b7280] dark:text-muted-foreground">
          © {new Date().getFullYear()} {brand.copyright}. Todos os direitos reservados.
        </p>
      </motion.div>
    </div>
  );
}
